/**
 * Centralized logging utility for the application.
 * Supports multiple log levels and module-based filtering.
 * 
 * ==========================================
 * USAGE INSTRUCTIONS
 * ==========================================
 * 
 * 1. INITIALIZATION (Required - call early in app startup):
 *    ```typescript
 *    import { configureLogger } from './logger-config.js';
 *    configureLogger(); // Auto-detects environment: WARN in production, INFO in development
 *    ```
 * 
 * 2. BASIC LOGGING IN YOUR MODULES:
 *    ```typescript
 *    import { Logger } from './utils/logger.js';
 *    
 *    const MODULE = 'MyComponent';
 *    
 *    Logger.debug(MODULE, 'Detailed state information', { someData: 123 });
 *    Logger.info(MODULE, 'User connected successfully');
 *    Logger.warn(MODULE, 'Retrying connection attempt', retryCount);
 *    Logger.error(MODULE, 'Failed to initialize device', error);
 *    ```
 * 
 * 3. PRODUCTION LOG REDUCTION:
 *    - Production automatically uses WARN level (~35 logs)
 *    - Development uses INFO level (~55 logs)
 *    - DEBUG disabled in production (saves ~110 logs)
 *    - 82% reduction: 200 logs → 35 logs in production
 * 
 * 4. TARGETED DEBUGGING (Optional):
 *    Enable debug logs for specific modules only:
 *    ```typescript
 *    import { enableDebugLogging } from './logger-config.js';
 *    enableDebugLogging(['RemoteCon', 'RemoteRec']); // Only these modules show DEBUG
 *    ```
 * 
 * 5. DISABLE ALL LOGGING:
 *    ```typescript
 *    import { disableAllLogging } from './logger-config.js';
 *    disableAllLogging(); // Silence everything
 *    ```
 * 
 * 6. MANUAL CONFIGURATION:
 *    ```typescript
 *    import { Logger, LogLevel } from './utils/logger.js';
 *    Logger.configure(LogLevel.DEBUG, ['RemoteCon', 'DeviceManager']);
 *    ```
 * 
 * 7. CHECK LOG LEVEL AT RUNTIME (for expensive operations):
 *    ```typescript
 *    if (Logger.isLevelEnabled(LogLevel.DEBUG)) {
 *        // Only compute expensive debug data if debug is enabled
 *        const expensiveData = computeExpensiveDebugInfo();
 *        Logger.debug(MODULE, 'Debug info', expensiveData);
 *    }
 *    ```
 * 
 * ==========================================
 * LOG LEVEL GUIDELINES
 * ==========================================
 * - DEBUG: WebRTC events, RPC calls, state changes (disabled in production)
 * - INFO: Connections, disconnections, device events (~55 in dev)
 * - WARN: Recoverable failures, missing configs (~20 always)
 * - ERROR: Critical failures, exceptions (~15 always)
 * - NONE: Disables all logging
 * 
 * ==========================================
 * MODULE NAMING CONVENTION
 * ==========================================
 * - Use descriptive module names: 'RemoteCon', 'RemoteRec', 'RPC', 'Controller'
 * - Keep names short but meaningful for easy filtering
 * - Define MODULE constant at top of each file:
 *   const MODULE = 'MyComponent';
 * 
 * ==========================================
 * EXISTING MODULE NAMES
 * ==========================================
 * - 'RemoteCon' - IntifaceRemoteCon.ts (controller-side RPC)
 * - 'RemoteRec' - IntifaceRemoteRec.ts (receiver-side RPC)
 * - 'RPC' - IntifaceRPCNode.ts (base RPC class)
 * - 'Controller' - IntifaceController.ts (device/connection manager)
 */

/** Log levels in order of severity */
export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3,
    NONE = 4
}

/**
 * Centralized logging utility with environment-aware filtering and module categorization.
 *
 * 
 * Usage:
 * ```typescript
 * import Logger from './utils/logger.js';
 * 
 * const MODULE = 'MyComponent';
 * Logger.info('Component initialized', MODULE);
 * Logger.debug('Detailed state info', MODULE, stateObj);
 * Logger.error('Operation failed', MODULE, error);
 * ```
 * 
 * Configuration:
 * ```typescript
 * // Development - show all logs from specific modules
 * Logger.configure(LogLevel.DEBUG, ['RemoteCon', 'RemoteRec']);
 * 
 * // Production - show only warnings and errors
 * Logger.configure(LogLevel.WARN);
 * 
 * // Disable all logging
 * Logger.configure(LogLevel.NONE);
 * ```
 */
class Logger {
    private static currentLevel: LogLevel = LogLevel.INFO;
    private static enabledModules = new Set<string>();
    
    /**
     * Configure the logger's behavior.
     * @param level - Minimum log level to display
     * @param modules - Optional whitelist of module names. If provided, only logs from these modules are shown.
     */
    static configure(level: LogLevel, modules?: string[]) {
        this.currentLevel = level;
        if (modules) {
            this.enabledModules = new Set(modules);
        } else {
            this.enabledModules.clear();
        }
    }
    
    /**
     * Check if a log should be displayed based on level and module filtering.
     */
    private static shouldLog(level: LogLevel, module?: string): boolean {
        // Check log level
        if (level < this.currentLevel) return false;
        
        // If module filtering is enabled, check if this module is allowed
        if (module && this.enabledModules.size > 0) {
            return this.enabledModules.has(module);
        }
        
        return true;
    }
    
    /**
     * Log debug information (verbose, typically disabled in production).
     * @param message - Log message
     * @param module - Optional module/component name for categorization
     * @param data - Additional data to log
     */
    static debug(message: string, module?: string, ...data: any[]) {
        if (this.shouldLog(LogLevel.DEBUG, module)) {
            const prefix = module ? `[${module}]` : '[App]';
            if (data.length > 0) {
                console.debug(`${prefix} ${message}`, ...data);
            } else {
                console.debug(`${prefix} ${message}`);
            }
        }
    }
    
    /**
     * Log informational messages (general application flow).
     * @param message - Log message
     * @param module - Optional module/component name for categorization
     * @param data - Additional data to log
     */
    static info(message: string, module?: string, ...data: any[]) {
        if (this.shouldLog(LogLevel.INFO, module)) {
            const prefix = module ? `[${module}]` : '[App]';
            if (data.length > 0) {
                console.info(`${prefix} ${message}`, ...data);
            } else {
                console.info(`${prefix} ${message}`);
            }
        }
    }
    
    /**
     * Log warnings (recoverable issues, deprecated usage, etc.).
     * @param message - Log message
     * @param module - Optional module/component name for categorization
     * @param data - Additional data to log
     */
    static warn(message: string, module?: string, ...data: any[]) {
        if (this.shouldLog(LogLevel.WARN, module)) {
            const prefix = module ? `[${module}]` : '[App]';
            if (data.length > 0) {
                console.warn(`${prefix} ${message}`, ...data);
            } else {
                console.warn(`${prefix} ${message}`);
            }
        }
    }
    
    /**
     * Log errors (exceptions, failures, critical issues).
     * @param message - Log message
     * @param module - Optional module/component name for categorization
     * @param error - Optional error object or additional data
     * @param data - Additional data to log
     */
    static error(message: string, module?: string, error?: any, ...data: any[]) {
        if (this.shouldLog(LogLevel.ERROR, module)) {
            const prefix = module ? `[${module}]` : '[App]';
            if (error || data.length > 0) {
                console.error(`${prefix} ${message}`, error, ...data);
            } else {
                console.error(`${prefix} ${message}`);
            }
        }
    }
    
    /**
     * Get current log level (useful for conditional verbose operations).
     */
    static getLevel(): LogLevel {
        return this.currentLevel;
    }
    
    /**
     * Check if a specific level is enabled.
     */
    static isLevelEnabled(level: LogLevel): boolean {
        return level >= this.currentLevel;
    }
}

export default Logger;
